import React, {
  forwardRef,
  PropsWithChildren,
  Ref,
  useImperativeHandle,
  useRef,
} from 'react';
import { ProjectType } from '@symfa-inc/providence-types';
import { Col, Form, Row } from 'antd';
import { YES_NO_GROUP } from '@models/constants';
import { SiteInformationType } from '@models/types';
import {
  Autocomplete,
  DatePicker,
  RadioGroup,
  ShowMoreInput,
} from '@shared/modules';
import { SiteInformationControlsProps } from '../../models/interfaces';
import {
  LTEControlsRefType,
  SiteInformationFormRefType,
} from '../../models/types';
import { LTEControls } from './lte-controls';
import { onSiteInfoFormChange, onSiteInfoFormSubmit } from './models';
import { NSBControls } from './nsb-controls';

export const SiteInformationForm = forwardRef(
  (
    {
      form,
      data: { siteAuditNote, ...data },
      projectType,
      isEditing,
      dropdownsOptions,
      permissions: {
        generalFields: {
          isSiteAuditRequired,
          scheduledSiteWalkDate,
          siteWalkCompleteDate,
          siteAuditCompletedBy,
          siteAuditNote: siteAuditNotePermissions,
          isFollowUpRequired,
          savedToFile,
          towerType,
          equipmentType,
        },
        LTEFields,
        NSBFields,
      },
      setIsSiteInfoCompare,
    }: PropsWithChildren<SiteInformationControlsProps>,
    forwardedRef: Ref<SiteInformationFormRefType>,
  ) => {
    const LTEControlsRef = useRef<LTEControlsRefType>(null);

    const unknownSwitchState = LTEControlsRef.current?.unknownSwitchState;

    useImperativeHandle(forwardedRef, () => ({
      onSubmit(): SiteInformationType {
        return onSiteInfoFormSubmit(
          form,
          { ...data, siteAuditNote },
          projectType,
          unknownSwitchState,
        );
      },
      // getter and setter unknownSwitchState
      unknownSwitchState: !!unknownSwitchState,
      changeUnknownSwitchState(value: boolean): void {
        LTEControlsRef.current?.changeUnknownSwitchState(value);
      },
      //
      setUnknownSwitch(): void {
        if (data?.isUnknownPowerPlantType !== undefined) {
          LTEControlsRef.current?.setUnknownSwitch(
            data?.isUnknownPowerPlantType,
          );
        }
      },
    }));

    return (
      <Form
        wrapperCol={{ span: 13 }}
        labelCol={{ span: isEditing ? 8 : 11 }}
        form={form}
        onValuesChange={(): void => {
          onSiteInfoFormChange(
            form,
            { ...data, siteAuditNote },
            setIsSiteInfoCompare,
            projectType,
            unknownSwitchState,
          );
        }}
      >
        <Row>
          <Col span="12">
            <RadioGroup
              id="isSiteAuditRequired"
              label="Site Audit Required"
              buttons={YES_NO_GROUP}
              isEditing={isEditing && !!isSiteAuditRequired}
            />
            <DatePicker
              id="scheduledSiteWalkDate"
              label="Scheduled Site Walk"
              isEditing={isEditing && !!scheduledSiteWalkDate}
            />
            <DatePicker
              id="siteWalkCompleteDate"
              label="Site Walk Complete"
              isEditing={isEditing && !!siteWalkCompleteDate}
            />
            <Autocomplete
              id="siteAuditCompletedBy"
              label="Site Audit Completed By"
              options={dropdownsOptions?.SiteAuditCompletedBy}
              isEditing={isEditing && !!siteAuditCompletedBy}
              elementProps={{ allowClear: true }}
            />
            <Autocomplete
              id="towerType"
              label="Tower Type"
              options={dropdownsOptions?.TowerType}
              isEditing={isEditing && !!towerType}
              elementProps={{ allowClear: true }}
            />
            <Autocomplete
              id="equipmentType"
              label="Equipment Type"
              options={dropdownsOptions?.EquipmentType}
              isEditing={isEditing && !!equipmentType}
              elementProps={{ allowClear: true }}
            />
            <ShowMoreInput
              id="siteAuditNote"
              label="Site Audit Note"
              data={siteAuditNote}
              form={form}
              isEditing={isEditing && !!siteAuditNotePermissions}
            />
            <RadioGroup
              id="isFollowUpRequired"
              label="Follow Up Required"
              buttons={YES_NO_GROUP}
              isEditing={isEditing && !!isFollowUpRequired}
            />
            <DatePicker
              id="savedToFileDate"
              label="Saved to File"
              isEditing={isEditing && !!savedToFile}
            />
          </Col>
          <Col span="12">
            {projectType === ProjectType.LTE ? (
              <LTEControls
                ref={LTEControlsRef}
                isEditing={isEditing}
                permissions={LTEFields}
                dropdownsOptions={dropdownsOptions}
              />
            ) : (
              <NSBControls
                isEditing={isEditing}
                permissions={NSBFields}
                dropdownsOptions={dropdownsOptions}
              />
            )}
          </Col>
        </Row>
      </Form>
    );
  },
);
